import { setAuth, setupAxios, removeInterceptors } from '../../api';
import {
    resourceGetAuth,
    resourceMariAuditTrail,
    resourceMariPing,
    resourceIsLoginAllowed,
    resourceAssignmentAreasHasSelector,
    resourceTabVisibility,
} from '../../api/resources';
import { setCookie, getCookie, removeCookie } from '../../cookies';
import { i18n } from '../../localization';
import router, { checkRouteAvailable } from '../../router';
import { AUTH_COOKIE, ROUTE_NAMES, SESSION_ID_COOKIE, USER_TYPES } from '../../constants';
import store from '../index';

const state = {
    user: {},
    authorized: false,
    isLoading: false,
    lastAuthError: null,
    lastAuthNotification: null,
    loginProgress: 0,
    tabVisibility: {},
    xMariTag: null,
};

const mutations = {
    SET_USER(state, user) {
        state.user = user;
        state.lastAuthError = null;
        state.lastAuthNotification = null;
    },

    CLEAR_USER(state) {
        state.user = {};
        state.authorized = false;
    },

    SET_ERROR(state, message) {
        state.lastAuthError = message;
    },

    SET_NOTIFICATION(state, { title, message }) {
        state.lastAuthNotification = { title, message };
    },

    CLEAR_ERROR(state) {
        state.lastAuthError = null;
    },

    CLEAR_NOTIFICATION(state) {
        state.lastAuthNotification = null;
    },

    SET_LOADING(state) {
        state.isLoading = true;
    },

    RESET_LOADING(state) {
        state.isLoading = false;
    },

    CHANGE_TOKEN(state, token) {
        state.user.token = token;
    },

    SET_AUTHORIZED() {
        state.authorized = true;
    },

    SET_TAB_VISIBILITY(state, val) {
        state.user.visibility = val;
    },

    SET_VISIBILITY_ADM(state, val) {
        state.tabVisibility = val;
    },

    SET_LOGIN_PROGRESS(state, val) {
        if (
            (!state.lastAuthError && val === undefined && state.loginProgress <= 85) ||
            (val !== undefined && state.loginProgress + val <= 100)
        ) {
            if (val !== undefined) {
                state.loginProgress = val;
            } else {
                state.loginProgress += 15;
            }
        }
    },

    SET_FIRST_NAME(state, val) {
        state.user.first_name = val;
    },

    SET_MARI_TAG(state, tag) {
        state.xMariTag = tag;
    },
};

/**
 * Creates prepared user state object compatible with the rest of the system
 * @param {object} userData
 * @param {string} token
 * @returns {{...userData, token: string, areaId: null|number, area: string}}
 */
function prepareUserState(userData, token) {
    let user = {
        ...userData,
        token,
        areaId: null,
        area: '',
    };

    if (userData.type !== USER_TYPES.SYS_ADMIN) {
        let area = userData.areas.find(item => item.primary);
        if (area) {
            user.areaId = area.id;
            user.area = area.name;
        }
    }

    return user;
}

/**
 * Authenticates user and fills user state on success or error otherwise
 * @param {string} token
 * @param {Function} commit
 * @param {Function} dispatch
 * @returns {Promise}
 */
function authorizeUser(token, commit, dispatch) {
    commit('SET_LOGIN_PROGRESS', 5);
    return dispatch('externals/loadExternalsConfiguration', null, { root: true })
        .then(() => {
            commit('SET_LOGIN_PROGRESS', 10);
            return resourceGetAuth(token);
        })
        .then(({ data }) => {
            setupAxios(token, dispatch, router.app.$t.bind(router.app));
            commit('SET_USER', prepareUserState(data, token));
            setCookie(AUTH_COOKIE, token);
            commit('SET_LOGIN_PROGRESS', 15);
            return resourceTabVisibility().then(res => {
                let visibility = {};
                Object.entries(res.data).forEach(
                    ([key, value]) => (visibility[key] = value.visible && value.enabled)
                );
                commit('SET_TAB_VISIBILITY', visibility);
                if (data.type === USER_TYPES.SYS_ADMIN) {
                    commit('SET_VISIBILITY_ADM', res.data);
                }
                return data;
            });
        })
        .then(() => {
            let data = state.user;
            let promiseQueue;
            let hasCheckIn = data.visibility.checkin && data.type !== USER_TYPES.SYS_ADMIN;
            commit('SET_LOGIN_PROGRESS', hasCheckIn ? 25 : 40);

            // TODO Think about error handling (https://jira.ascom-ws.com/browse/MERA-1584)
            let p1 = dispatch('navigation/loadOrganizationName', {}, { root: true }).then(() =>
                commit('SET_LOGIN_PROGRESS')
            );
            let p2 = dispatch('loadColorSettings', null, { root: true }).then(() =>
                commit('SET_LOGIN_PROGRESS')
            );
            let p3 = dispatch('loadAssignmentAreas', null, { root: true }).then(() =>
                commit('SET_LOGIN_PROGRESS')
            );
            let p4 = dispatch('loadCallTypes', null, { root: true }).then(() => commit('SET_LOGIN_PROGRESS'));

            let p5 = dispatch('loadMARiCurrentTime', null, { root: true });

            let p6 = dispatch('loadTimeFormat', null, { root: true });

            promiseQueue = [p1, p2, p3, p4, p5, p6];
            if (data.type === USER_TYPES.SUPER_USER) {
                // First ping on login/refresh
                if (getCookie(SESSION_ID_COOKIE)) {
                    promiseQueue.push(
                        resourceMariPing({
                            method: 'post',
                            data: JSON.stringify({
                                session_id: getCookie(SESSION_ID_COOKIE),
                            }),
                        }).catch(() => {
                            commit('SET_NOTIFICATION', {
                                title: i18n.t('login.pingLogoutTitle'),
                                message: i18n.t('login.pingLogoutMessage'),
                            });
                            return Promise.reject({ response: i18n.t('login.pingLogoutTitle') });
                        })
                    );
                }
                promiseQueue.push(
                    dispatch('loadSuperuserAreas', null, { root: true }).then(() =>
                        commit('SET_LOGIN_PROGRESS')
                    )
                );
            }
            if (data.type !== USER_TYPES.SYS_ADMIN) {
                promiseQueue.push(
                    resourceAssignmentAreasHasSelector({
                        id: data.areas[0].id,
                    }).then(res => {
                        data.visibility.duty_selector = !res.data.has_selector && res.data.have_duties;
                        dispatch('loadDutySelectorState', data.areaId, { root: true });
                    })
                );
            }
            if (hasCheckIn) {
                promiseQueue.push(
                    dispatch(
                        'checkin/loadNotCheckedInInfo',
                        { id: state.user.areaId },
                        { root: true }
                    ).then(() => commit('SET_LOGIN_PROGRESS'))
                );
            }
            if (!getCookie(SESSION_ID_COOKIE)) {
                // Prevent extra 'login' action on page refersh
                // and if login is not allowed for current super user
                // Log 'login' action and get session_id
                if (data.type === USER_TYPES.SUPER_USER) {
                    // Check first for active super user session
                    promiseQueue.push(
                        resourceIsLoginAllowed()
                            .then(() => {
                                promiseQueue.push(
                                    resourceMariAuditTrail({
                                        method: 'post',
                                        data: JSON.stringify({
                                            action: 'login',
                                        }),
                                    })
                                        .then(response => {
                                            setCookie(
                                                SESSION_ID_COOKIE,
                                                (response && response.data && response.data.session_id) ||
                                                    null
                                            );
                                        })
                                        .then(() => {
                                            resourceMariPing({
                                                method: 'post',
                                                data: JSON.stringify({
                                                    session_id: getCookie(SESSION_ID_COOKIE),
                                                }),
                                            });
                                        })
                                );
                            })
                            .catch(() => {
                                return Promise.reject({ response: i18n.t('login.superUserLoginNotAllowed') });
                            })
                    );
                } else {
                    promiseQueue.push(
                        resourceMariAuditTrail({
                            method: 'post',
                            data: JSON.stringify({
                                action: 'login',
                            }),
                        }).then(response => {
                            setCookie(
                                SESSION_ID_COOKIE,
                                (response && response.data && response.data.session_id) || null
                            );
                        })
                    );
                }
            }

            return Promise.all(promiseQueue).then(() => {
                commit('SET_LOGIN_PROGRESS', 0);
                commit('SET_AUTHORIZED');
            });
        })
        .catch(error => {
            console.log(error);
            commit('SET_LOGIN_PROGRESS', 0);
            let errorMessage = i18n.t('common.networkError');
            if (error.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                let contentTypeHeader = error.response.headers ? error.response.headers['content-type'] : '';
                switch (contentTypeHeader) {
                    case 'text/html':
                        errorMessage = error.response.statusText;
                        break;
                    case 'application/json':
                        errorMessage = error.response.data
                            ? i18n.t(error.response.data.error)
                            : error.response;
                        break;
                    default:
                        errorMessage = error.response.data ? error.response.data : error.response;
                        break;
                }
            } else if (error.request) {
                // The request was made but no response was received
                // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
                // http.ClientRequest in node.js
                errorMessage =
                    error.request.status === 0 ? i18n.t('common.networkError') : error.request.responseText;
            }
            commit('CLEAR_USER');
            commit('SET_ERROR', errorMessage);
            removeCookie(AUTH_COOKIE);
            removeCookie(SESSION_ID_COOKIE);
        });
}

/**
 * Converts Unicode string to UTF-8 packed to base64
 * @see https://developer.mozilla.org/en-US/docs/Web/API/WindowBase64/Base64_encoding_and_decoding#The_Unicode_Problem
 * @param {string} str
 * @returns {string}
 */
const b64EncodeUnicode = str =>
    window.btoa(
        encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) => String.fromCharCode('0x' + p1))
    );

/**
 * Creates authorization token from login and password (UTF-8 encoding is used)
 * @param {string} username
 * @param {string} password
 * @returns {string}
 */
const createToken = (username, password) => b64EncodeUnicode(username + ':' + password);

const getters = {
    userAreaId: state => state.user.areaId,
    userId: state => state.user.user_id,
    userType: state => state.user.type,
    userArea: state => state.user.area,
};

const actions = {
    /**
     * Checks authorization on page load (first load or refresh). Returns resolved
     * promise immediately if no authorization token is present or checks it and fills
     * user state otherwise
     * @param commit
     * @param dispatch
     * @returns {Promise}
     */
    checkIfLogged({ commit, dispatch }) {
        let token = getCookie(AUTH_COOKIE);
        if (token) {
            commit('SET_LOADING');
            return authorizeUser(token, commit, dispatch).then(() => {
                commit('RESET_LOADING');
            });
        }

        return Promise.resolve();
    },

    /**
     * Logs new user in
     * @param commit
     * @param dispatch
     * @param state
     * @param {string} username
     * @param {string} password
     * @returns {Promise}
     */
    login({ commit, dispatch, state }, { username, password }) {
        return authorizeUser(createToken(username, password), commit, dispatch).then(() => {
            if (
                state.authorized &&
                (!router.currentRoute.name || !checkRouteAvailable(state.user, router.currentRoute.name))
            ) {
                router.push({ name: ROUTE_NAMES.ERROR_404 });
                store.commit('SHOW_LOADING', false);
            }
        });
    },

    /**
     * Logs user out
     * @param commit
     * @param dispatch
     * @param {boolean} redirect redirect to root page after logout
     */
    logout({ commit, dispatch }, redirect = true) {
        commit('CLEAR_USER');
        commit('CLEAR_ERROR');
        if (getCookie(SESSION_ID_COOKIE)) {
            resourceMariAuditTrail({
                method: 'post',
                data: JSON.stringify({
                    action: 'logout',
                    session_id: getCookie(SESSION_ID_COOKIE),
                }),
            })
                .catch(() => {
                    console.log('logout catch');
                })
                .then(() => {
                    removeCookie(SESSION_ID_COOKIE);
                });
        }
        commit('SET_WIDGET_ID', null, { root: true });
        dispatch('checkin/clearTimeoutId', {}, { root: true });
        removeCookie(AUTH_COOKIE);
        document.title = 'MARi';
        if (redirect && router.currentRoute.name !== ROUTE_NAMES.DASHBOARD) {
            router.push('/');
        }
        removeInterceptors();
    },

    /**
     * Changes password for currently logged in user
     * @param state
     * @param commit
     * @param {string} newPassword
     */
    changePassword({ state, commit }, newPassword) {
        let token = createToken(state.user.login_name, newPassword);

        commit('CHANGE_TOKEN', token);
        setAuth(token);
    },

    /**
     * Checks if current app token is different from session token.
     * Logs user out when no session token is present.
     * Fetches new auth data to store when session token was changed.
     * @param state
     * @param dispatch
     * @param commit
     * @returns {Promise<boolean>}
     */
    checkToken({ state, dispatch, commit }) {
        let stateToken = state.user.token;
        let cookieToken = getCookie(AUTH_COOKIE);

        if (stateToken === cookieToken) {
            return Promise.resolve(true);
        }

        if (cookieToken !== undefined) {
            return authorizeUser(cookieToken, commit, dispatch).then(() => Promise.resolve(true));
        }

        dispatch('logout', false);

        return Promise.resolve(false);
    },

    /**
     * Changes first name for currently logged in user
     * @param commit
     * @param {string} newFirstName
     */
    setFirstName({ commit }, newFirstName) {
        commit('SET_FIRST_NAME', newFirstName.trim());
    },

    /**
     * Set MARi tag
     * @param commit
     * @param {string} tag
     */
    setMariTag({ commit }, tag) {
        commit('SET_MARI_TAG', tag);
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    getters,
    actions,
};
